import { useEffect, useState } from 'react';
import {
  extractDocxPlainText,
  getFileExtension,
  getSliderPreviewKind,
  loadAttachmentArrayBuffer,
} from '../../utils/fileUtils';

const MAX_TEXT_PREVIEW_BYTES = 512 * 1024;
const MAX_TEXT_PREVIEW_CHARS = 120_000;
const MAX_DOCX_PREVIEW_BYTES = 8 * 1024 * 1024;

const formatTextPreview = (raw, fileName) => {
  const ext = getFileExtension(fileName);
  if (ext === 'json') {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  return raw;
};

const truncatePreview = (raw) => (
  raw.length > MAX_TEXT_PREVIEW_CHARS
    ? `${raw.slice(0, MAX_TEXT_PREVIEW_CHARS)}\n\n… preview truncated`
    : raw
);

export const useAttachmentPreview = (attachment) => {
  const kind = attachment ? getSliderPreviewKind(attachment) : 'none';
  const [pdfUrl, setPdfUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState(null);

  useEffect(() => {
    if (!attachment) {
      setPdfUrl(null);
      setTextContent(null);
      setTextLoading(false);
      setTextError(null);
      return undefined;
    }

    if (kind === 'pdf') {
      setTextContent(null);
      setTextLoading(false);
      setTextError(null);
      let revoked = false;
      let createdUrl = null;
      (async () => {
        try {
          const buffer = await loadAttachmentArrayBuffer(attachment);
          const url = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
          createdUrl = url;
          if (revoked) {
            URL.revokeObjectURL(url);
            return;
          }
          setPdfUrl(url);
        } catch {
          if (!revoked) setPdfUrl(null);
        }
      })();
      return () => {
        revoked = true;
        if (createdUrl) URL.revokeObjectURL(createdUrl);
        setPdfUrl(null);
      };
    }

    if (kind === 'text' || kind === 'docx') {
      setPdfUrl(null);
      setTextContent(null);
      setTextError(null);
      setTextLoading(true);
      let cancelled = false;
      (async () => {
        try {
          const sizeLimit = kind === 'docx' ? MAX_DOCX_PREVIEW_BYTES : MAX_TEXT_PREVIEW_BYTES;
          if (attachment.fileSize != null && attachment.fileSize > sizeLimit) {
            if (!cancelled) {
              setTextError('File is too large to preview here. Download to open it.');
              setTextLoading(false);
            }
            return;
          }
          const buffer = await loadAttachmentArrayBuffer(attachment);
          if (buffer.byteLength > sizeLimit) {
            if (!cancelled) {
              setTextError('File is too large to preview here. Download to open it.');
              setTextLoading(false);
            }
            return;
          }
          let raw;
          if (kind === 'docx') {
            raw = await extractDocxPlainText(buffer);
            if (!raw) {
              throw new Error('empty docx');
            }
          } else {
            raw = formatTextPreview(
              new TextDecoder('utf-8', { fatal: false }).decode(buffer),
              attachment.fileName
            );
          }
          if (!cancelled) {
            setTextContent(truncatePreview(raw));
            setTextLoading(false);
          }
        } catch {
          if (!cancelled) {
            setTextError(
              kind === 'docx'
                ? 'Could not preview this Word file. Download to open it in Word.'
                : 'Could not load text preview'
            );
            setTextLoading(false);
          }
        }
      })();
      return () => { cancelled = true; };
    }

    setPdfUrl(null);
    setTextContent(null);
    setTextLoading(false);
    setTextError(null);
    return undefined;
  }, [attachment, kind]);

  return { kind, pdfUrl, textContent, textLoading, textError };
};
